import AffiliateMembershipMenuItems from '@/components/affiliate/AffiliateMembershipMenuItems';
import StepTransition from '@/components/motion/StepTransition';
import { RowActionsMenu } from '@/components/table/RowActionsMenu';
import { MenuItem, MenuItemGroup, MenuSeparator } from '@/components/ui/menu';
import { Tooltip } from '@/components/ui/tooltip';
import { Box, Span } from '@chakra-ui/react';
import { AffiliateProgramStatus, type AffiliateUserListItem } from '@fluenceur/types';
import { NavArrowLeft } from 'iconoir-react';
import { useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';

type Program = AffiliateUserListItem['programs'][number];
type Action = 'manage' | 'pause' | 'resume' | 'remove';
interface Props {
  programs: Program[];
  canManage: boolean;
  triggerRef: Ref<HTMLButtonElement>;
  onAction: (program: Program, action: Action) => void;
}

/** Action first, then an explicit programme choice; both levels stay in one popup. */
export default function AffiliateProgramActionMenu({
  programs,
  canManage,
  triggerRef,
  onAction,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<Action | null>(null);
  const [highlighted, setHighlighted] = useState<string | null>(null);
  const [navigated, setNavigated] = useState(false);
  const [direction, setDirection] = useState<'inline-forward' | 'inline-backward'>(
    'inline-forward'
  );
  if (programs.length === 0) {
    const reason = t('affiliates.membershipActions.noActions');
    return (
      <Tooltip content={reason} portalled>
        <Box as="span" display="inline-flex" tabIndex={0} aria-label={reason}>
          <RowActionsMenu
            label={t('affiliates.removal.programActions')}
            triggerRef={triggerRef}
            disabled
          >
            {null}
          </RowActionsMenu>
        </Box>
      </Tooltip>
    );
  }
  const multiple = programs.length > 1;
  const eligible = (next: Action) =>
    programs.filter((program) => {
      if (next === 'manage') return true;
      if (program.status !== AffiliateProgramStatus.ENROLLED) return false;
      if (next === 'remove') return canManage;
      return next === 'pause' ? program.isActive : !program.isActive;
    });
  const labels: Record<Action, string> = {
    manage: t('affiliates.membershipActions.manage'),
    pause: t('affiliates.membershipActions.pause'),
    resume: t('affiliates.membershipActions.resume'),
    remove: t('affiliates.removal.action'),
  };
  const selectAction = (next: Action) => {
    if (!multiple) {
      const program = eligible(next)[0];
      if (program) onAction(program, next);
      return;
    }
    setDirection('inline-forward');
    setNavigated(true);
    setAction(next);
    setHighlighted(`program:${eligible(next)[0]?.id}`);
  };
  const back = () => {
    setDirection('inline-backward');
    setNavigated(true);
    setHighlighted(`action:${action}`);
    setAction(null);
  };
  const actions = (
    <AffiliateMembershipMenuItems
      valuePrefix="action"
      programName={programs[0]?.name ?? ''}
      compactManage
      nextStep={multiple}
      onManage={programs.length ? () => selectAction('manage') : undefined}
      onPause={eligible('pause').length ? () => selectAction('pause') : undefined}
      onResume={eligible('resume').length ? () => selectAction('resume') : undefined}
      onRemove={eligible('remove').length ? () => selectAction('remove') : undefined}
    />
  );
  const content = action ? (
    <>
      <MenuItem value="back" closeOnSelect={false} onSelect={back} aria-label={t('common.back')}>
        <NavArrowLeft width={15} height={15} />
        <Span fontWeight="medium">{labels[action]}</Span>
      </MenuItem>
      <MenuSeparator />
      <MenuItemGroup title={t('affiliates.membershipActions.chooseProgram')}>
        {eligible(action).map((program) => (
          <MenuItem
            key={program.id}
            value={`program:${program.id}`}
            aria-label={program.name}
            title={program.name}
            onSelect={() => onAction(program, action)}
          >
            <Span lineClamp={2} overflowWrap="anywhere">
              {program.name}
            </Span>
          </MenuItem>
        ))}
      </MenuItemGroup>
    </>
  ) : multiple ? (
    actions
  ) : (
    <MenuItemGroup title={programs[0]?.name}>{actions}</MenuItemGroup>
  );
  return (
    <RowActionsMenu
      label={t('affiliates.removal.programActions')}
      triggerRef={triggerRef}
      contentProps={{
        w: '260px',
        maxW: 'calc(100vw - 24px)',
        overflow: 'hidden',
        onKeyDownCapture: (event) => {
          // Ark keeps DOM focus on the menu content, outside the inner step wrapper.
          if (action && event.key === 'ArrowLeft') {
            event.preventDefault();
            event.stopPropagation();
            back();
          }
        },
      }}
      menuProps={{
        open,
        highlightedValue: highlighted,
        onHighlightChange: ({ highlightedValue }) => setHighlighted(highlightedValue),
        onOpenChange: ({ open: next }) => {
          setOpen(next);
          setAction(null);
          setNavigated(false);
          setHighlighted(null);
        },
      }}
    >
      <Box>
        {navigated ? (
          <StepTransition stepKey={action ?? 'actions'} direction={direction}>
            {content}
          </StepTransition>
        ) : (
          content
        )}
      </Box>
    </RowActionsMenu>
  );
}
